import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { FolderOpen, Plus, RefreshCw } from 'lucide-react';
import { ProjectListSkeleton } from '../common/SkeletonLoader';
import { Button, Spinner } from '../ui';
import projectService from '../../services/projectService';
import ProjectsDataTable from './ProjectsDataTable';
import SetWorkflowModal from '../modals/SetWorkflowModal';

const API_BASE = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';

const ProjectsView = ({
  activeService,
  myProjects,
  projectsLoading,
  sortField,
  sortDirection,
  fetchMyProjects,
  setActiveService,
  handleSort,
  handleViewProject,
  handleDeleteProject,
  user = null,
}) => {
  const [openDropdownId, setOpenDropdownId] = useState(null);
  const dropdownRef = useRef(null);
  const [adminUsers, setAdminUsers] = useState([]);
  const [assigneeLoadingId, setAssigneeLoadingId] = useState(null);
  const [workflowModalProject, setWorkflowModalProject] = useState(null);
  const isKeywordView = activeService === 'keyword-projects';
  const isSynopsisView = activeService === 'synopsis-projects';
  const isTranscriptionView = activeService === 'transcription-projects';
  const showAssigneeColumn = isKeywordView || isSynopsisView || isTranscriptionView;
  const canCreateTasks = user?.role === 'admin' || user?.role === 'pm' || user?.app_metadata?.role === 'admin' || user?.app_metadata?.role === 'pm';

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setOpenDropdownId(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Load admin users for assignee dropdown when on keyword/synopsis projects and user can create tasks (admin/pm)
  useEffect(() => {
    if (!showAssigneeColumn || !canCreateTasks) {
      setAdminUsers([]);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const response = await axios.get(`${API_BASE}/admin/users?minimal=true`);
        if (!cancelled) {
          const list = Array.isArray(response.data?.users) ? response.data.users : [];
          setAdminUsers(list);
        }
      } catch (error) {
        if (!cancelled) setAdminUsers([]);
      }
    })();
    return () => { cancelled = true; };
  }, [showAssigneeColumn, canCreateTasks]);

  // Filter projects based on active service
  const filteredProjects = activeService === 'synopsis-projects'
    ? myProjects.filter(p => p.projectType === 'synopsis' || p.projectType === 'synopsis_generation' || p.projectType === 'synopsis_comparison')
    : activeService === 'transcriber-projects'
    ? myProjects.filter(p => p.projectType === 'transcription' || !p.projectType)
    : activeService === 'keyword-projects'
    ? myProjects.filter(p => p.projectType === 'keyword' || p.projectType === 'keyword_generation')
    : activeService === 'transcription-projects'
    ? myProjects.filter(p => p.projectType === 'transcription_generation')
    : myProjects;

  // Sort projects
  const sortProjects = (projects, field, direction) => {
    return [...projects].sort((a, b) => {
      let aValue, bValue;
      
      switch (field) {
        case 'title':
          aValue = (a.title || '').toLowerCase();
          bValue = (b.title || '').toLowerCase();
          break;
        case 'createdAt':
          aValue = new Date(a.createdAt);
          bValue = new Date(b.createdAt);
          break;
        case 'status':
          const aIsKeyword = a.projectType === 'keyword' || a.projectType === 'keyword_generation';
          const bIsKeyword = b.projectType === 'keyword' || b.projectType === 'keyword_generation';
          const aIsTranscription = a.projectType === 'transcription_generation';
          const bIsTranscription = b.projectType === 'transcription_generation';
          const aIsSuccess = aIsKeyword || aIsTranscription
            ? (a.status || 'active').toLowerCase() === 'active'
            : (a.projectType === 'synopsis' || a.projectType === 'synopsis_generation' || a.projectType === 'synopsis_comparison')
              ? (a.fileCount > 0 && (a.synopsisCount || 0) > 0)
              : (a.fileCount > 0 && a.transcriptCount > 0);
          const bIsSuccess = bIsKeyword || bIsTranscription
            ? (b.status || 'active').toLowerCase() === 'active'
            : (b.projectType === 'synopsis' || b.projectType === 'synopsis_generation' || b.projectType === 'synopsis_comparison')
              ? (b.fileCount > 0 && (b.synopsisCount || 0) > 0)
              : (b.fileCount > 0 && b.transcriptCount > 0);
          aValue = aIsSuccess ? 1 : 0;
          bValue = bIsSuccess ? 1 : 0;
          break;
        case 'language':
          aValue = (a.language || '').toLowerCase();
          bValue = (b.language || '').toLowerCase();
          break;
        case 'assignee': {
          const aName = a.assigneeName ?? a.keyword_assignee_name ?? a.synopsis_assignee_name ?? a.transcription_assignee_name ?? '';
          const bName = b.assigneeName ?? b.keyword_assignee_name ?? b.synopsis_assignee_name ?? b.transcription_assignee_name ?? '';
          aValue = (aName || '').toLowerCase();
          bValue = (bName || '').toLowerCase();
          break;
        }
        case 'workflow': {
          const aName = a.workflowStatusDisplayName ?? a.workflow_status_display_name ?? a.workflowStatus ?? a.workflow_status ?? '';
          const bName = b.workflowStatusDisplayName ?? b.workflow_status_display_name ?? b.workflowStatus ?? b.workflow_status ?? '';
          aValue = (aName || '').toLowerCase();
          bValue = (bName || '').toLowerCase();
          break;
        }
        default:
          return 0;
      }
      
      if (direction === 'asc') {
        return aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
      } else {
        return aValue > bValue ? -1 : aValue < bValue ? 1 : 0;
      }
    });
  };

  const getTitle = () => {
    if (activeService === 'synopsis-projects') return 'Synopsis Projects';
    if (activeService === 'transcriber-projects') return 'Transcriber Projects';
    if (activeService === 'keyword-projects') return 'Keyword Projects';
    if (activeService === 'transcription-projects') return 'Transcription Projects';
    return 'My Projects';
  };

  const getDescription = () => {
    if (activeService === 'synopsis-projects') return 'Manage your synopsis generation projects';
    if (activeService === 'transcriber-projects') return 'Manage your transcription projects';
    if (activeService === 'keyword-projects') return 'Manage your keyword generation projects';
    if (activeService === 'transcription-projects') return 'Manage your transcription projects';
    return 'Manage your ASR transcription projects';
  };

  const getProjectCount = () => {
    return filteredProjects.length;
  };

  return (
    <>
        <div className="flex items-center">
          <div>
            <h3 className="text-xl font-semibold text-gray-900 dark:text-neutral-100">
              {getTitle()}
            </h3>
            <div className="flex items-center gap-2 mt-1">
              <p className="text-gray-600 dark:text-neutral-400 mb-3">
                {getDescription()}
              </p>
              <span className="bg-sky-100 dark:bg-sky-900 text-sky-800 dark:text-sky-200 text-xs font-medium px-2.5 py-0.5 rounded-full">
                {getProjectCount()} projects
              </span>
            </div>
          </div>
        </div>
      <div key="projects-service-interface" className="bg-white dark:bg-neutral-950 border border-gray-200 dark:border-neutral-800 rounded-lg p-8 shadow-sm">
      {/* Service Header */}
      <div className="flex items-center justify-between mb-6">

        <div className="flex space-x-3">
          <Button
            variant="default"
            size="sm"
            onClick={() => fetchMyProjects(false)}
            disabled={projectsLoading}
            title="Smart refresh: Uses Redis cache for instant response, falls back to database if needed"
          >
            {projectsLoading ? (
              <Spinner size="sm" className="text-current" />
            ) : (
              <RefreshCw className="w-4 h-4" />
            )}
            Refresh
          </Button>
          {activeService === 'synopsis-projects' && canCreateTasks && (
            <Button
              variant="default"
              size="sm"
              onClick={() => setActiveService('synopsis')}
            >
              <Plus className="w-4 h-4" />
              New Synopsis
            </Button>
          )}
        </div>
      </div>
      
      {/* Projects List */}
      <div className="space-y-6">
        {projectsLoading ? (
          <ProjectListSkeleton count={5} />
        ) : filteredProjects.length === 0 ? (
          <div className="bg-gray-50 dark:bg-neutral-900 border-2 border-dashed border-gray-300 dark:border-neutral-700 rounded-lg p-8 text-center">
            <FolderOpen className="w-12 h-12 text-gray-400 dark:text-neutral-500 mx-auto mb-4" />
            <h4 className="text-lg font-medium text-gray-900 dark:text-neutral-100 mb-2">No Projects Yet</h4>
            <p className="text-gray-600 dark:text-neutral-400 text-sm mb-4">
              {canCreateTasks
                ? (activeService === 'synopsis-projects'
                  ? 'Complete your first synopsis generation to see it here'
                  : activeService === 'transcriber-projects'
                  ? 'Complete your first transcription to see it here'
                  : 'Complete your first ASR transcription to see it here')
                : 'Only admin and PM can create new tasks. You can view existing projects.'}
            </p>
            {canCreateTasks && (
              <button
                onClick={() => setActiveService(activeService === 'synopsis-projects' ? 'synopsis' : 'asr')}
                className="bg-sky-600 hover:bg-sky-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors"
              >
                {activeService === 'synopsis-projects' ? 'Start Synopsis Generation' : 'Start Transcription'}
              </button>
            )}
          </div>
        ) : (
          <div className="space-y-6">
            <ProjectsDataTable
              data={sortProjects(filteredProjects, sortField, sortDirection)}
              sortField={sortField}
              sortDirection={sortDirection}
              handleSort={handleSort}
              showAssigneeColumn={showAssigneeColumn}
              canCreateTasks={canCreateTasks}
              canDeleteProjects={user?.role === 'admin' || user?.app_metadata?.role === 'admin'}
              canSetWorkflow={canCreateTasks}
              onSetWorkflow={setWorkflowModalProject}
              adminUsers={adminUsers}
              assigneeLoadingId={assigneeLoadingId}
              setAssigneeLoadingId={setAssigneeLoadingId}
              openDropdownId={openDropdownId}
              setOpenDropdownId={setOpenDropdownId}
              dropdownRef={dropdownRef}
              handleViewProject={handleViewProject}
              handleDeleteProject={handleDeleteProject}
              projectService={projectService}
              fetchMyProjects={fetchMyProjects}
            />
            <SetWorkflowModal
              isOpen={!!workflowModalProject}
              onClose={() => setWorkflowModalProject(null)}
              projectId={workflowModalProject?.id}
              projectType={workflowModalProject?.projectType ?? workflowModalProject?.project_type}
              isLocked={(workflowModalProject?.workflowStatus ?? workflowModalProject?.workflow_status) === 'delivered'}
              onSaved={fetchMyProjects}
            />
          </div>
        )}
      </div>
    </div>
    </>
  );
};

export default ProjectsView;

